import {
  toWSDate,
  WSElement,
  WSGrid,
  WSList,
  WSPage,
  WSPageToolbar,
  WSPageToolbarAction,
  WSPanel,
  WSPoster,
  WSSectionToolbar,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerPayeeEngagementStatus } from "@wingspanhq/payments/dist/interfaces";
import { PayerPayeeStatus } from "@wingspanhq/payments/dist/interfaces/payerPayee";
import { UserStatus } from "@wingspanhq/users/dist/lib/interfaces";
import { wsName } from "@wingspanhq/utils/dist/name/wsName";
import React, { useState } from "react";
import { Route, RouteComponentProps, Switch } from "react-router-dom";
import { Tabs } from "../../../../components/Tabs";
import { useUserId } from "../../../../query/hooks/helpers";
import { useFeatureFlags } from "../../../../query/hooks/useFeatureFlags";
import { useIntegrationsQuickbooks } from "../../../../query/integrations/queries";
import { useQueryPayee } from "../../../../query/payee/queries/useQueryPayee";
import { usePayeeEngagementsListByPayeeIdQuery } from "../../../../query/payeeEngagements/queries/usePayeeEngagementsListByPayeeIdQuery";
import { useRequirementsListQuery } from "../../../../query/requirements/queries/useRequirementsListQuery";
import { useGetOrganizationUser } from "../../../../query/users/queries";
import { WSQueries } from "../../../../query/WSQuery";
import { ResourceHistory } from "../../../../shared/components/ResourceHistory";
import { useIsEngagementsActive } from "../../../../shared/hooks/useIsEngagementsActive";
import { selectorIsQBOActive } from "../../../../shared/selectors/selectorIsQBOActive";
import { useAuthorizedScopeGroups } from "../../../../shared/utils/teamUtils";
import { useGoTo } from "../../../../shared/utils/useGoTo";
import { DocumentsSection } from "../../../Payers/components/DocumentsSection";
import { DocumentsSectionType } from "../../../Payers/components/DocumentsSection/types";
import { PayeeIntegrationWidget } from "../../../Payers/components/IntegrationsSection/PayeeIntegrationWidget";
import { selectorIsAllRequirementsComplete } from "../../../Requirements/selectors/selectorIsAllRequirementsComplete";
import { AutopayWidget } from "../../components/AutopayWidget/AutopayWidget";
import { ContractorInformation } from "../../components/ContractorInformation";
import { useCreatePayeeEngagementModal } from "../../components/CreatePayeeEngagementModal/useCreateEngagementModal";
import { CustomFieldsWidget } from "../../components/CustomFieldsWidget/CustomFieldsWidget";
import { DeductionsWidget } from "../../components/DeductionWidget";
import { ESignatureWidget } from "../../components/ESignatureWidget";
import { InternalNotesWidget } from "../../components/InternalNotesWidget";
import { RecentPayablesWidget } from "../../components/RecentPayablesWidget";
import { TaxFilingWidget } from "../../components/TaxFilingWidget/TaxFilingWidget";
import { usePayeeHistoryResources } from "../../hooks/usePayeeHistoryResources";
import { getPayeeNames } from "../../selectors/getPayeeNames";
import { selectorPayeeEngagementsTmp } from "../../selectors/selectorPayeeEngagementsTmp";
import { selectorPayerPayeeEngagements } from "../../selectors/selectorPayerPayeeEngagements";
import {
  buildPayeeDetailsPath,
  buildPayeeDetailsRequirementsPath,
  buildPayeeEditTaxInfoPath,
  buildPayeeHistoryPath,
  PAYEES_ARCHIVED_PATH,
  PAYEES_DETAILS_REQUIREMENTS_ROUTE,
  PAYEES_DETAILS_ROUTE,
  PAYEES_LIST_PATH
} from "../../utils";
import { PayerPayeeDetailsRequirements } from "../../../Requirements/components/PayerPayeeDetailsRequirements";
import { EngagementsTable } from "./EngagementsTable";
import { UnavailableOrgWidget } from "./UnavailableOrgWidget";
import { usePayeeRowActions } from "./usePayeeRowActions";
import { useXWingspanPayerId } from "./useXWingspanPayerId";
import { isPayeeProvidedInfo } from "./utils";
import { RequirementContextType } from "../../../Requirements/types";
import { selectorPayeeHasEligibleEngagements } from "../../selectors/selectorPayeeHasEligibleEngagements";
import { PayeeDetailsAlerts } from "./PayeeDetailsAlerts";

export const RoutePayeeDetails: React.FC<
  RouteComponentProps<{
    payeeId: string;
  }>
> = ({ match, history, location }) => {
  const goTo = useGoTo();
  const { payeeId } = match.params;

  const organizationId = useXWingspanPayerId();

  const isOrgContext = !!organizationId;
  const getPayeeActions = usePayeeRowActions();

  const userId = useUserId();
  const queryPayee = useQueryPayee(payeeId);
  const queryEngagements = usePayeeEngagementsListByPayeeIdQuery(payeeId);
  const queryOrganization = useGetOrganizationUser(organizationId!, {
    enabled: isOrgContext && organizationId !== userId
  });
  const modalAddEngagement = useCreatePayeeEngagementModal();
  const isEngagementsActive = useIsEngagementsActive();

  const { hasPaymentsScope } = useAuthorizedScopeGroups();

  const queryQBO = useIntegrationsQuickbooks();
  const queryFeatureFlags = useFeatureFlags({
    enabled: queryQBO.isFetched
  });

  const [showArchived, setShowArchived] = useState<boolean>(false);

  const historyResources = usePayeeHistoryResources(payeeId);

  const queryRequirements = useRequirementsListQuery({
    filter: {
      payerId: userId,
      payeeId,
      payerPayeeEngagementIds: {
        "!=": "null"
      }
    },
    sort: {
      createdAt: "asc"
    },
    page: {
      size: 99
    }
  });

  const queryArchivedRequirements = useRequirementsListQuery({
    filter: {
      payerId: userId,
      payeeId,
      payerPayeeEngagementIds: "null"
    },
    sort: {
      createdAt: "asc"
    },
    page: {
      size: 99
    }
  });

  return (
    <WSPage>
      <WSQueries
        queries={{
          queryPayee,
          queryFeatureFlags
        }}
      >
        {({ queryPayeeData, queryFeatureFlagsData }) => {
          const secondaryLoading =
            queryEngagements.isFetching || queryRequirements.isFetching;
          const tertiaryLoading = queryArchivedRequirements.isFetching;

          const engagements = queryEngagements.data || [];
          const requirements = queryRequirements.data || [];
          const payeeRequirements = queryPayeeData.requirements || [];

          const names = getPayeeNames(queryPayeeData);

          const isQuickbooksActive = selectorIsQBOActive(
            queryFeatureFlagsData,
            queryQBO.data
          );

          const isInactive =
            queryPayeeData.payerOwnedData.status === PayerPayeeStatus.Inactive;

          const hasEligibleEngagements = queryEngagements.data
            ? selectorPayeeHasEligibleEngagements(engagements)
            : false;

          const forceShowAllEngagements =
            isQuickbooksActive ||
            engagements.some(
              eng => eng.status === PayerPayeeEngagementStatus.Inactive
            );

          const filteredEngagements = isEngagementsActive
            ? selectorPayerPayeeEngagements(engagements, false, true)
            : selectorPayeeEngagementsTmp(
                engagements,
                forceShowAllEngagements,
                false,
                true
              );

          const activeFilteredEngagements = filteredEngagements.filter(
            eng => eng.status === PayerPayeeEngagementStatus.Active
          );
          const inactiveFilteredEngagements = filteredEngagements.filter(
            eng => eng.status === PayerPayeeEngagementStatus.Inactive
          );

          const headerActions: WSPageToolbarAction[] = getPayeeActions({
            payee: queryPayeeData,
            backPath: location.pathname
          });

          const organizationName = queryOrganization.data
            ? wsName({
                user: queryOrganization.data
              }).getResolvedName(true)
            : undefined;

          const isAllRequirementsComplete = selectorIsAllRequirementsComplete([
            ...requirements,
            ...payeeRequirements
          ]);

          return (
            <>
              <WSPageToolbar
                breadcrumbs={[
                  {
                    label: "Contractors",
                    onClick: () => {
                      history.push(PAYEES_LIST_PATH);
                    }
                  },
                  ...(organizationName
                    ? [
                        {
                          label: organizationName,
                          onClick: () => {
                            history.push(
                              PAYEES_LIST_PATH +
                                `?payerId%5B0%5D=${organizationId}`
                            );
                          }
                        }
                      ]
                    : []),
                  ...(queryPayeeData.payerOwnedData.status ===
                  PayerPayeeStatus.Inactive
                    ? [
                        {
                          label: "Archived contractors",
                          onClick: () => {
                            history.push(PAYEES_ARCHIVED_PATH);
                          }
                        }
                      ]
                    : []),
                  { label: names.getResolvedName() }
                ]}
                title={names.getResolvedName()}
                titlePill={
                  queryPayeeData.payerOwnedData.status ===
                  PayerPayeeStatus.Inactive
                    ? {
                        theme: "neutral",
                        text: "Archived",
                        icon: false
                      }
                    : queryPayeeData.user?.status === UserStatus.Active
                    ? { theme: "success", text: "Signed-up", icon: false }
                    : {
                        theme: "warning",
                        text: "Not signed-up",
                        icon: false
                      }
                }
                titleAfter={
                  <WSText.ParagraphXs color="gray500">
                    Last modified{" "}
                    {toWSDate(queryPayeeData.updatedAt).formattedValue}
                  </WSText.ParagraphXs>
                }
                actions={headerActions}
              />
              <WSList gap="L">
                <WSElement shimmer={secondaryLoading}>
                  <PayeeDetailsAlerts
                    payee={queryPayeeData}
                    requirements={requirements}
                    engagements={engagements}
                  />
                </WSElement>

                <WSGrid gutter="XL">
                  <WSGrid.Item span={{ m: "8" }}>
                    <Tabs
                      mb="L"
                      tabs={[
                        {
                          label: "Summary",
                          path: buildPayeeDetailsPath(payeeId)
                        },
                        {
                          label: "Requirements",
                          pill: {
                            icon: true,
                            theme: isAllRequirementsComplete
                              ? "success"
                              : "warning"
                          },
                          path: buildPayeeDetailsRequirementsPath(payeeId)
                        }
                      ]}
                    />

                    <Switch>
                      <Route
                        path={PAYEES_DETAILS_ROUTE}
                        exact
                        render={() => (
                          <WSList gap="XL">
                            <WSElement>
                              <WSSectionToolbar
                                pill={{
                                  text: isPayeeProvidedInfo(queryPayeeData)
                                    ? "Contractor shared"
                                    : "Provided by you",
                                  theme: isPayeeProvidedInfo(queryPayeeData)
                                    ? "blue"
                                    : "neutral"
                                }}
                                title="Contractor information"
                                button={
                                  isPayeeProvidedInfo(queryPayeeData)
                                    ? undefined
                                    : {
                                        label: "Edit",
                                        onClick: () => {
                                          goTo(
                                            buildPayeeEditTaxInfoPath(payeeId)
                                          );
                                        }
                                      }
                                }
                              />
                              <WSPanel mt="S">
                                <WSList gap="2XL">
                                  <ContractorInformation
                                    payee={queryPayeeData}
                                    editable
                                  />

                                  {/*
                        <WSInfoBox>
                          {isPayeeProvidedInfo(queryPayeeData)
                            ? `Contractor shared: last updated on ${toWSDateString(
                                queryPayeeData.updatedAt
                              )}`
                            : `Payer provided: last updated on ${toWSDateString(
                                queryPayeeData.updatedAt
                              )}`}
                        </WSInfoBox>
                        */}
                                </WSList>
                              </WSPanel>
                            </WSElement>
                            <WSList gap="L">
                              {isInactive ? null : (
                                <WSElement>
                                  <WSSectionToolbar
                                    title="Engagements"
                                    mb="M"
                                    button={
                                      isEngagementsActive
                                        ? {
                                            label: "Add engagement",
                                            onClick: () => {
                                              modalAddEngagement.open({
                                                payeeId: queryPayeeData.payeeId
                                              });
                                            }
                                          }
                                        : undefined
                                    }
                                  />
                                  {activeFilteredEngagements.length ? (
                                    <EngagementsTable
                                      engagements={activeFilteredEngagements}
                                      payee={queryPayeeData}
                                    />
                                  ) : (
                                    <WSPanel>
                                      <WSPoster
                                        size="S"
                                        type="tasks"
                                        title="No engagements active"
                                        description="Once an engagement is added and active, it will appear here. Payments can still be made directly to contractors without engagements."
                                      />
                                    </WSPanel>
                                  )}
                                </WSElement>
                              )}
                              {inactiveFilteredEngagements.length ? (
                                <WSElement>
                                  <WSSectionToolbar
                                    title={
                                      showArchived || isInactive
                                        ? "Archived engagements"
                                        : ""
                                    }
                                    mb="M"
                                    buttons={
                                      isInactive
                                        ? undefined
                                        : [
                                            {
                                              label: showArchived
                                                ? `Hide archived (${inactiveFilteredEngagements.length})`
                                                : `Show archived (${inactiveFilteredEngagements.length})`,
                                              onClick: () => {
                                                setShowArchived(v => !v);
                                              }
                                            }
                                          ]
                                    }
                                  />
                                  {isInactive || showArchived ? (
                                    <EngagementsTable
                                      engagements={inactiveFilteredEngagements}
                                      payee={queryPayeeData}
                                    />
                                  ) : null}
                                </WSElement>
                              ) : null}
                            </WSList>
                          </WSList>
                        )}
                      />
                      <Route
                        path={PAYEES_DETAILS_REQUIREMENTS_ROUTE}
                        render={() => (
                          <WSElement shimmer={tertiaryLoading}>
                            <PayerPayeeDetailsRequirements
                              requirements={requirements}
                              hasEligibleEngagements={hasEligibleEngagements}
                              noActions={isOrgContext}
                              archivedRequirements={
                                queryArchivedRequirements.data || []
                              }
                              contextType={RequirementContextType.Payee}
                              status={queryPayeeData.payerOwnedData.status}
                              goToRequirement={requirement =>
                                history.push(
                                  `/member/payees/${queryPayeeData.payeeId}/requirement/${requirement.requirementId}`
                                )
                              }
                            />
                          </WSElement>
                        )}
                      />
                    </Switch>
                  </WSGrid.Item>
                  <WSGrid.Item span={{ m: "4" }}>
                    <WSList gap="XL">
                      {hasPaymentsScope ? (
                        isOrgContext ? null : (
                          <RecentPayablesWidget payeeId={payeeId} />
                        )
                      ) : null}

                      <InternalNotesWidget payee={queryPayeeData} />

                      <CustomFieldsWidget
                        payee={queryPayeeData}
                        isInactive={isInactive}
                      />

                      <TaxFilingWidget payeeId={payeeId} />

                      {isOrgContext ? null : (
                        <WSElement shimmer={secondaryLoading}>
                          <ESignatureWidget
                            payee={queryPayeeData}
                            engagements={engagements}
                          />
                        </WSElement>
                      )}

                      <DocumentsSection
                        isInactive={isInactive}
                        payeeId={queryPayeeData.payeeId}
                        payerId={userId}
                        type={DocumentsSectionType.Collaborator}
                      />

                      {isOrgContext ? null : (
                        <DeductionsWidget
                          clientId={userId}
                          memberId={queryPayeeData.payeeId}
                          onRowClick={row => {
                            history.push(
                              `/member/invoices/payables/deductions/scheduled/${row.deductionId}`
                            );
                          }}
                          onShowAllClick={() => {
                            history.push(
                              `/member/invoices/payables/deductions/scheduled`
                            );
                          }}
                        />
                      )}

                      <ResourceHistory
                        resources={historyResources}
                        onViewAll={() => {
                          history.push(buildPayeeHistoryPath(payeeId));
                        }}
                      />

                      <AutopayWidget
                        isInactive={isInactive}
                        payee={queryPayeeData}
                      />

                      {isOrgContext ? null : (
                        <PayeeIntegrationWidget
                          isInactive={isInactive}
                          payeeId={queryPayeeData.payeeId}
                          vendorId={
                            queryPayeeData.payerOwnedData.integration
                              ?.quickbooks?.vendorId
                          }
                          expenseAccountId={
                            queryPayeeData.payerOwnedData.integration
                              ?.quickbooks?.expenseAccountId
                          }
                        />
                      )}

                      {isOrgContext ? (
                        <>
                          <UnavailableOrgWidget title="Recent payables" />
                          <UnavailableOrgWidget title="Scheduled deductions" />
                          <UnavailableOrgWidget title="Integration mappings" />
                        </>
                      ) : null}
                    </WSList>
                  </WSGrid.Item>
                </WSGrid>
              </WSList>
            </>
          );
        }}
      </WSQueries>
    </WSPage>
  );
};
